import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getContact, saveContact } from "@/lib/waitlist";
import { API } from "@/App";

export const WaitlistDialog = ({ cta, resultId, onClose }) => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [fallback, setFallback] = useState(null);
  const name = cta?.program || cta?.name;

  useEffect(() => {
    if (!cta) return;
    const c = getContact();
    setEmail(c.email || "");
    setFirstName(c.firstName || "");
    setDone(false);
    setFallback(null);
  }, [cta]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    saveContact({ email, firstName });
    try {
      await axios.post(`${API}/results/${resultId}/waitlist`, { item_key: cta.item_key, email, first_name: firstName || null });
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.fallback_url) setFallback(d.fallback_url);
      else toast.error(typeof d === "string" ? d : "Please enter a valid email address.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!cta} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#FFFDF9] border-[#F0C4D0] rounded-3xl sm:max-w-lg" data-testid="waitlist-dialog">
        {fallback ? (
          <div className="text-center py-6" data-testid="waitlist-fallback">
            <p className="font-serif text-2xl text-[#800534]">Complete your waitlist signup</p>
            <p className="fea-body mt-3">Please finish joining the {name} waitlist on our secure signup page.</p>
            <a href={fallback} target="_blank" rel="noopener noreferrer" onClick={onClose} className="fea-btn mt-6" data-testid="waitlist-fallback-link">Open the Waitlist Form</a>
          </div>
        ) : done ? (
          <div className="text-center py-6" data-testid="waitlist-success">
            <CheckCircle2 className="h-12 w-12 mx-auto text-[#800534]" />
            <p className="font-serif text-2xl text-[#800534] mt-4">You're on the waitlist</p>
            <p className="fea-body mt-3">Thank you for saying yes to {name}. Please check your inbox; if a confirmation email arrives, click it to secure your place.</p>
            <button onClick={onClose} className="fea-btn mt-6" data-testid="waitlist-close-button">Return to My Results</button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="waitlist-form">
            <DialogHeader>
              <p className="fea-overline">{cta?.item_key === "group_coaching" ? "Group coaching waitlist" : "Course waitlist"}</p>
              <DialogTitle className="font-serif text-2xl sm:text-3xl text-[#800534] font-semibold" data-testid="waitlist-dialog-title">{name}</DialogTitle>
              <DialogDescription className="fea-body">Join the waitlist and you'll be among the first to know when enrollment opens.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-5">
              <input className="fea-input" placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={60} data-testid="waitlist-first-name-input" />
              <input className="fea-input" type="email" required placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="waitlist-email-input" />
            </div>
            <button type="submit" disabled={busy} className="fea-btn w-full mt-5" data-testid="waitlist-confirm-button">
              {busy && <Loader2 className="h-5 w-5 animate-spin" />} Yes, Add Me to the Waitlist
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
